import React, { useMemo, useState } from 'react'
import { DateRange as Picker } from 'react-date-range';
import { format, isSameDay, isToday, parse } from 'date-fns';
import TextField from '../era/TextField';
import { IconButton, InputAdornment, Popover, useTheme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarRange, faXmark } from '@fortawesome/pro-regular-svg-icons';

const DATE_DISPLAY_FMT = "yyyy-MMM-dd"

const initializeSelection = (fieldValue, dateFormat = DATE_DISPLAY_FMT) => {
  const ref = new Date()
  const returnValue = {
    startDate: ref,
    endDate: ref,
    key: 'selection'
  }
  if (!!fieldValue) {
    if (typeof fieldValue === 'string') {
      const [start, end] = fieldValue.split(' - ')
      returnValue.startDate = parse(start, dateFormat, ref)
      returnValue.endDate = parse(end, dateFormat, ref)
    } else {
      returnValue.startDate = new Date(fieldValue.startDate)
      returnValue.endDate = new Date(fieldValue.endDate)
    }
  }

  return returnValue
}

export const DateRangePicker = ({
  pickerProps, value, dateFormat = DATE_DISPLAY_FMT,
  onChange = () => { },
  ...props
}) => {
  const theme = useTheme()
  const selected = useMemo(() => {
    if (!value)
      return [initializeSelection(value, dateFormat)]
    return [
      { ...value, key: 'selection' }
    ]
  }, [value, dateFormat])
  const [anchorEl, setAnchorEl] = useState(null)
  const displayValue = useMemo(() => {
    const { startDate, endDate } = selected[0] || {}
    if (isSameDay(startDate, endDate) && isToday(startDate))
      return '' // default value; return empty string
    return `${format(startDate, dateFormat)} - ${format(endDate, dateFormat)}`
  }, [selected, dateFormat])

  const handleTextFieldClick = e => {
    setAnchorEl(e.target)
  }

  const handleDateChange = newValue => {
    const newSelection = newValue.selection
    onChange({
      startDate: newSelection.startDate,
      endDate: newSelection.endDate,
    })
  }

  const handleClear = e => {
    e.stopPropagation()
    onChange(null)
  }

  return <>
    <TextField fullWidth {...props}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {!!displayValue &&
              <IconButton size='small' title='Clear date range' onClick={handleClear}>
                <FontAwesomeIcon icon={faXmark} size='sm' />
              </IconButton>
            }
            <FontAwesomeIcon icon={faCalendarRange} size='lg' />
          </InputAdornment>
        ),
        ...props.InputProps
      }}
      onClick={handleTextFieldClick}
      value={displayValue}
      readOnly
    />
    <Popover open={!!anchorEl} anchorEl={anchorEl}
      onClose={() => setAnchorEl(null)}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
    >
      <Picker ranges={selected}
        onChange={handleDateChange}
        showDateDisplay={false}
        style={{
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.text.primary,
        }}
        color={theme.palette.primary.main}
        rangeColors={[theme.palette.primary.main]}
      />
    </Popover>
  </>
}